import React, { useEffect, useMemo } from 'react';
import { appStore, chatState, usePresenceStore, userStore } from '../zustand';
import { useRoomManager } from '../hooks/useRoomManager';
import { ChannelProvider } from 'ably/react';
import { NotificationChannel } from '../zustand/const';
import { AblyProvider } from 'ably/react';
import { getAuthToken, makeRequest } from '../util/AxiosInstance';
import { createRealtimeClient } from './AblyClient';
import * as PusherPushNotifications from "@pusher/push-notifications-web";
import { isEmpty } from 'underscore';
import { beamsClient } from './PusherBeamClient';
import { isIOS } from '../util/functions';
import { InstantDb } from '../zustand/instant.schema';
import { toast } from 'sonner';
import { AppNameSpaces, AppRooms } from '../types/app-rooms.enum';


const InitWrapper = ({ children }) => {
    const { user, setChannel, token } = userStore();
    const { getUserRoom } = useRoomManager()
    const mode = appStore((state) => state.mode);
    const setRoomId = chatState((state) => state.setRoomId);
    const setRoomInfo = chatState((state) => state.setRoomInfo);
    const setPresence = usePresenceStore((state) => state.setPresence);

    const { isLoading, user: instantUser, error } = InstantDb.useAuth();

    const realtimeClient = useMemo(() => (token ? createRealtimeClient() : null), [token]);

    const getToken = async () => {
        const token = await makeRequest("GET", "/instant/token", { id: user.id })
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.log(err);
            });

        if (token) {
            InstantDb.auth.signInWithToken(token);
        } else {
            toast.error("Error in getting token");
        }
    };

    useEffect(() => {
        const init = async () => {
            if (!isIOS()) {
                let user = userStore.getState().user;

                beamsClient
                    .start()
                    .then(async () => {
                        let jwt = await getAuthToken();
                        const beamsTokenProvider = new PusherPushNotifications.TokenProvider({
                            url: `${import.meta.env.VITE_API_URL}/pusher/token`,
                            headers: {
                                Authorization: `Bearer ${jwt}`,
                            },
                        });
                        return beamsTokenProvider
                    })
                    .then(async (beamsTokenProvider) => {
                        if (user && 'email' in user && !isEmpty(user.email)) {
                            await beamsClient.setUserId(user.email, beamsTokenProvider).then(() => {
                                // console.log('Successfully registered and subscribed to pusher!');
                            }).catch(console.error);
                        } else {
                            console.log('No user found')
                        }
                    })
                    .then(() => {
                        if (user) {
                            beamsClient
                                .getUserId()
                                .then((userId) => {
                                    if (userId !== user.email) {
                                        return beamsClient.stop();
                                    }
                                })
                                .catch(console.error);
                        }
                    })
                    .then(() => {
                        if (mode === 'admin') {
                            beamsClient.addDeviceInterest('admin').then(() => {
                            }).catch(console.error);
                        }
                    })
                    .catch(console.error);
            }
        }
        try {
            init()
        } catch (error) {
            console.log('Error initializing beams client: ', error);
        }
    }, []);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (error) {
            toast.error("Error in getting token");
            return;
        }
        getToken();
    }, [isLoading, error]);

    const online = InstantDb.room(AppRooms.PRESENCE, AppNameSpaces.NAME)
    const presence = InstantDb.rooms.usePresence(online);
    InstantDb.rooms.useSyncPresence(online, user);

    useEffect(() => {
        const uniquePeers = Array.from(
            Object.values(presence.peers)
                .filter((p) => p.id) // Ensure peer has an id
                .reduce((map, peer) => {
                    map.set(peer.id, peer);
                    return map;
                }, new Map())
                .values()
        );
        setPresence(uniquePeers);
    }, [presence]);

    useMemo(() => {
        const init = async () => {
            if (user && 'id' in user) {
                let room = await getUserRoom(`${user.id}`)
                if (!room) {
                    console.error('Room not found')
                    return;
                }
                setRoomId(room.id)
                setRoomInfo(room)
            }
        }

        if (!instantUser) {
            return;
        }
        if (user) {
            setChannel(`channel:${user.id}`)
        }
        init()
    }, [user, instantUser]);

    return (
        <>
            {realtimeClient ? (
                <AblyProvider client={realtimeClient}>
                    <ChannelProvider channelName={NotificationChannel}>
                        {children}
                    </ChannelProvider>
                </AblyProvider>
            ) : (
                <>{children}</>
            )}
        </>
    );
};

export default InitWrapper;